/**
 * Types of Currency
 */

export const supportedCurrencies = [
	'USD',
	'INR',
	'EUR',
	'IDR',
	'AUD',
	'GBP',
] as const

type CurrencyTypeTuple = typeof supportedCurrencies

export type Currency = CurrencyTypeTuple[number]

export type CurrencyType = 'USD' | 'INR' | 'EUR' | 'IDR' | 'AUD' | 'GBP'

/**
 * Pricing Types
 */

export type VendorType =
	| 'GUPSHUP'
	| 'GUPSHUP_ENTERPRISE'
	| 'THREE_SIXTY_DIALOG'
	| 'THREE_SIXTY_DIALOG_CLOUD'
	| 'WHATSAPP_CLOUD'
	| 'AIRTEL'
	| 'PINNACLE'
	| 'PINNACLE_V3'

export type ChannelType = 'WHATSAPP' | 'BIZ_CONVERSATION'

export interface MessagingPrice {
	_id?: string
	country_code: string
	country: string
	phone_code: string
	vendor: VendorType
	channel: ChannelType
	base_currency: CurrencyType
	vendor_per_message_incoming: number
	vendor_per_message_outgoing: number
	vendor_charge: number
	whatcx_charge: number
	uic_charge: number
	bic_charge: number
	media_charge: number
	whatsapp_template_marketing: number
	whatsapp_template_authentication: number
	whatsapp_template_utility: number
	whatsapp_service: number
	createdAt?: string
	updatedAt?: string
}

export interface PricingFilters {
	vendor?: VendorType
	base_currency?: CurrencyType
	country_code?: string
	phone_code?: string
	channel?: ChannelType
}

export interface CreatePricingData
	extends Omit<MessagingPrice, '_id' | 'createdAt' | 'updatedAt'> {}

export interface UpdatePricingData extends Partial<CreatePricingData> {}

export interface BulkPricingUpdate {
	_id: string
	data: UpdatePricingData
}

/**
 * Transaction Type
 */

export type TransactionType = 'DEBIT' | 'CREDIT' | 'RESERVE' | 'RESERVE_REFUND'

/**
 * All Modules
 */

export type Modules =
	| 'BROADCAST'
	| 'CONVERSATION'
	| 'FEEDBACK'
	| 'MANAGEMENT'
	| 'WHATSY'
	| 'INTEGRATION'
	| 'FORM'
	| 'WIDGET'

/**
 * Type Definition for MobileNumber
 */

export interface MobileNumber {
	country: string
	mobile: string
	code: number
}

/**
 * Type Definition for Client Wallet Amount
 */

export interface ClientWalletAmount {
	credit: string
	reserve: string
}

/**
 * Type Definition for Client Wallet
 */

export interface ClientWallet {
	active: boolean
	amount: ClientWalletAmount
	createdAt: string
	createdBy: string
	updatedAt: string
	_id: string
	__v: number
}

/**
 * Type for Admin User Using the Client Management Dashboard
 */

export interface AdminUser {
	_id: string
	refreshToken: string
	token: string
	profile: null
	active: boolean
	twoFactorAuth: boolean
	role: null
	lastLogin: string
	superAdmin: false
	lastName: string
	firstName: string
	mobile: string
	email: string
	createdAt: string
	updatedAt: string
	__v: number
}

/**
 * Billing Cycles
 */

export const allBillingCycle = [
	'MONTHLY',
	// TODO: Enable before invoice revamp
	// 'QUARTERLY',
	// 'HALF_YEARLY',
	'YEARLY',
] as const

type BillingCycleTuple = typeof allBillingCycle

export type BillingCycle = BillingCycleTuple[number]

/**
 * Plan Types
 */

export type PlanType =
	| 'KICKSTART'
	| 'VELOCITY'
	| 'ACCELERATION'
	| 'TRANSFORMATION'

/**
 * Plan Module Type
 */

export const allowedModuleTypes = [
	'CONTACT',
	'CONVERSATION',
	'TEMPLATE',
	'INTEGRATION',
	'ENQUIRY',
	'BOT',
	'FORM',
	'BROADCAST',
	'QR_WIDGET',
	'CUSTOM_FORM_INPUT',
	'ROLES_AND_PERMISSION',
	'CONVERSATION_LABEL',
	'CONTACT_TAGS',
	'APPS',
	'FEEDBACK',
	'RELATIONSHIP_MODULE',
	'REPORTING_MODULE',
	'USER',
	'TEAM',
	'EDGE_CX_AI',
	'INTELLIGENCE',
	'AI_CHATBOT',
	'AI_SUMMARIZER',
	'EXTERNAL_BOT',
	'SIMPLE_BOT',
	'OOOH_BOT',
	'SMART_ROUTER_BOT',
	'WINY_BACKUP',
	'VOICE_CALLING',
	'REVENUE_OPPORTUNITIES',
	'TICKETS_BOT',
	'WEBHOOKS',
	'SIGNALS',
	'NOTIFIER',
	'DRIP',
	'SLA_BOT',
	'GROUP_ANALYTICS',
] as const

type AllowedModuleTuple = typeof allowedModuleTypes

export type AllowedModuleType = AllowedModuleTuple[number]

export interface AllowedModule {
	type: AllowedModuleType
	count: number
	childCount?: number
	totalContact?: number
}

/**
 * Type for plan in the WhatCX Application
 */

export interface Plan {
	_id: string
	title: string
	visible: boolean
	planType: 'SYSTEM' | 'CREATED'
	allowedModules: AllowedModule[] | null
	/**
	 * @deprecated - Modules are depecrated in favor of allowedModules
	 */
	modules: {
		broadCast: boolean
		conversation: boolean
		integration: boolean
		feedback: boolean
		enquiry: boolean
	} | null
	perYear: number
	perMonth: number
	billingCycle: BillingCycle
	storageAllowed: number
	templateAllowed: number
	teamAllowed: number
	userAllowed: number
	planName: string
	createdAt: string
	updatedAt: string
	__v: number
	voiceCalling?: {
		enabled: boolean
		pricing?: {
			currency: string
			virtualNumberSetupFee: number
			virtualNumberMonthlyRental: number
			perChannelMonthlyRental: number
			numberOfChannels: number
			inboundPerMinuteRate: number
			outboundPerMinuteRate: number
		}
	}
	aiServices?: {
		enabled: boolean
		pricing?: {
			currency: string
			perTokenRate: number
			perTranscriptionMinuteRate?: number
			perDocumentIntelligenceUnitRate?: number
		}
	}
}

/**
 * Types of Current Limit
 */

// ['TIER_0K', 'TIER_1K', 'TIER_10K', 'TIER_100K', 'TIER_UNLIMITED'],

export type CurrentLimit =
	| 'TIER_0K'
	| 'TIER_1K'
	| 'TIER_10K'
	| 'TIER_100K'
	| 'TIER_UNLIMITED'

/**
 * App Vendors Available
 */

export type AppVendor = 'LEAD_SQUARE' | 'ZOHO_CRM' | 'LS_WHATSAPP_CONNECTOR'

/**
 * Client Invoice Object Reference
 */
export interface ClientInvoiceDetails {
	key: string
	value: string
}

/**
 * Client Taxation Object Reference
 */
export interface ClientTaxation {
	key: string
	percentage: number
}

/**
 * Relationship Manager
 */

export interface RelationShipManager
	extends Pick<
		AdminUser,
		'_id' | 'email' | 'firstName' | 'lastName' | 'mobile'
	> {}

/** Number of Employes Types */

export const numberOfEmployesTypes = [
	'SELF_EMPLOYEED',
	'1-10',
	'11-50',
	'51-200',
	'201-500',
	'501-1000',
	'1001-5000',
	'5001-10,000',
	'10,001+',
] as const

type NumberOfEmployesTypesTuple = typeof numberOfEmployesTypes

export type NumberOfEmployesType = NumberOfEmployesTypesTuple[number]

/**
 * Edge Platform Types
 *
 * 'DASH_CX' | 'EDGE_CX' | 'TURBO_CX'
 */

export const edgePlatforms = ['DASH_CX', 'EDGE_CX', 'TURBO_CX'] as const

type EdgePlatformTuple = typeof edgePlatforms

export type EdgePlatform = EdgePlatformTuple[number]

export const platformStacks = ['V1', 'V2'] as const

type PlatformStackTuple = typeof platformStacks

export type PlatformStack = PlatformStackTuple[number]

/**
 * Type for Client using the WhatCX Application
 */

export interface ClientProfile {
	_id: string
	__v: number
	active: boolean
	activePlan: Plan
	address: string
	apiKey: null | string
	appName: null | string
	wabaId: null | string
	airtelCustomerId: null | string
	airtelUsername: null | string
	airtelPassword: null | string
	city: string
	clientId: string
	country: string
	createdAt: string
	createdBy: string
	currentLimit: CurrentLimit
	email: string
	isFreeTrial: boolean
	limitUpdatedAt: null | string
	logo: null | string
	mobileNumber: MobileNumber
	organizationName: string
	planHistory: Plan[] | []
	sourceWaNumber: null | string
	state: string
	storageUsed: number
	updatedAt: string
	vendorSelected: VendorType
	wallet: ClientWallet
	webhookUrl: null | string
	website: string
	gupshup_user_id: null | string
	gupshup_user_password: null | string
	gupshup_two_way_user: null | string
	gupshup_two_way_password: null | string
	wa_token: null | string
	wa_phone_id: null | string
	ts_apiKey: null | string
	access: string
	currency_selected: Currency
	qrCode: null | string
	uid_key: string
	waPhoneStatus: 'NA' | 'FLAGGED' | 'RESTRICTED'
	waQualityRating: 'RED' | 'YELLOW' | 'GREEN'
	webhookToken: string
	appSessionToken?: null | string
	insufficientBalanceNotify?: null | boolean
	invoiceDetails?: ClientInvoiceDetails[]
	isWaVerified?: boolean
	is_account_setup?: boolean
	is_template_sample_deleted?: boolean
	lowWalletBalanceNotify?: null | boolean
	nextBillingDate?: null | string
	notificationEmails?: string
	invoiceEmails?: string
	relationShipManager?: string[] | RelationShipManager[]
	taxation?: ClientTaxation[]
	wa_profile_image?: string
	whatcxCharge?: number
	whatcxChargeInbound?: number
	whatcxChargeOutbound?: number
	// Coexistence fields
	coexistenceEnabled?: boolean
	isCoexistenceOnboarding?: boolean
	coexistenceOnboardedAt?: string | null
	historyShared?: boolean
	coexistenceWebhookFields?: string[]
	// App Related Keys
	activeAppIntegration?: AppVendor[]
	channels?: {
		whatsapp_api: boolean
		voice_enablex?: boolean
	}
	enablexVoice?: {
		appId: string
		appKey: string
		fromNumber: string
		webhookUrl?: string
		isConfigured: boolean
	}
	aiPlatformConfig?: {
		credentialMode: 'shared' | 'byok'
		openai?: {
			apiKey?: string
			defaultModel?: string
		}
	}
	ls_connector_webhook?: {
		callback_persist: boolean
		sent_status: boolean
		webhooks: {
			max_concurrent_requests: number
			url: string
		}
	}
	accountSuspended?: boolean
	archived?: boolean
	archivedAt?: string | null
	shouldGenerateAutoInvoice?: boolean
	isFreePlan?: boolean
	whatsyTrigger?: boolean
	name?: string
	numberOfEmployees?: NumberOfEmployesType
	isEmailVerified?: boolean
	optInWhatsAppCheck?: boolean
	pinCode?: null
	assetsAdded?: boolean
	businessManager?: boolean
	developerApp?: boolean
	ensureBusinessPage?: boolean
	fbAccountId?: string
	fbPageUrl?: string
	onBoardingStep: number
	onBoardingType: 'FREE' | 'PAID'
	setupWebook?: boolean
	systemUserCreated?: boolean
	whatsAppEnable?: boolean
	setupWebhook?: boolean
	selectedEDGEPlatform?: EdgePlatform
	platformStack?: PlatformStack
	tdsEnabled?: boolean
	broadcastLimit: number
	nextStep?: number
	tokenMetaData?: null | string
	utmSource?: null | string
	utmCampaign?: null | string
	utmMedium?: null | string
	referedBy?: null | string
	referCode?: null | string
	platformMode?: 'DEMO' | 'LIVE'
	ts_channelId?: string | null
	setupPayment?: boolean
	demoExpirationTimestamp?: number
	metadata?: null | Object
	subscription?: 'SUBSCRIPTION'
	nextRenewalTimestamp?: number
	ts_clientId?: string | null
	ts_payload?: null | {
		template_messaging_enabled?: {
			id: 'EuRgz4xmuQySTQPW'
			event: 'template_messaging_enabled'
			data: {
				id: 'MTBICFCH'
				setup_info: {
					phone_number: '919472045918'
					phone_name: 'Zsites Enterprises'
				}
				status: 'ready'
				account_mode: 'live'
				created_at: '2023-08-24T11:31:13Z'
				billing_started_at: '2023-08-24T11:31:25Z'
				cancelled_at: null
				terminated_at: null
				client_id: 'TvqvohYRCL'
				current_quality_rating: null
				current_limit: null
				has_inbox: false
				is_oba: false
				hub_status: 'live'
				version: 2
				is_migrated: false
				settings: null
				client: {
					id: 'TvqvohYRCL'
					name: 'Rakesh Kumar Chaudhary'
					contact_info: {
						email: 'rakesh.chaudhary+360test1@edgecx.com'
					}
					partner_payload: '64e73b0cde7e2bbb69649d3e'
				}
				waba_account: {
					id: 'OJhN7lWA'
					on_behalf_of_business_info: null
					fb_account_status: 'not_verified'
					namespace: 'fcdb139c_86f1_4d36_84bf_fb85dc4ecd4b'
					external_id: '105024229369767'
					fb_business_id: '996231914910425'
				}
				integration: {
					enabled: true
					state: 'setting_up'
					app_id: '124286'
					hosting_platform_type: 'meta_cloud_api'
				}
			}
		}
		channel_subscription_set?: {
			id: '2fkrWxmuAT0GlDDM'
			event: 'channel_subscription_set'
			data: {
				id: 'MTBICFCH'
				setup_info: {
					phone_number: '919472045918'
					phone_name: 'Zsites Enterprises'
				}
				status: 'ready'
				account_mode: 'live'
				created_at: '2023-08-24T11:31:13Z'
				billing_started_at: '2023-08-24T11:31:25Z'
				cancelled_at: null
				terminated_at: null
				client_id: 'TvqvohYRCL'
				current_quality_rating: null
				current_limit: null
				has_inbox: false
				is_oba: false
				hub_status: 'live'
				version: 2
				is_migrated: false
				settings: null
				client: {
					id: 'TvqvohYRCL'
					name: 'Rakesh Kumar Chaudhary'
					contact_info: {
						email: 'rakesh.chaudhary+360test1@edgecx.com'
					}
					partner_payload: '64e73b0cde7e2bbb69649d3e'
				}
				waba_account: {
					id: 'OJhN7lWA'
					on_behalf_of_business_info: null
					fb_account_status: 'not_verified'
					namespace: 'fcdb139c_86f1_4d36_84bf_fb85dc4ecd4b'
					external_id: '105024229369767'
					fb_business_id: '996231914910425'
				}
				integration: {
					enabled: true
					state: 'setting_up'
					app_id: '124286'
					hosting_platform_type: 'meta_cloud_api'
				}
			}
		}
		channel_ready?: {
			id: 'b9fXSrFyU0z6UXDM'
			event: 'channel_ready'
			data: {
				id: 'MTBICFCH'
				setup_info: {
					phone_number: '919472045918'
					phone_name: 'Zsites Enterprises'
				}
				status: 'ready'
				account_mode: 'live'
				created_at: '2023-08-24T11:31:13Z'
				billing_started_at: null
				cancelled_at: null
				terminated_at: null
				client_id: 'TvqvohYRCL'
				current_quality_rating: null
				current_limit: null
				has_inbox: false
				is_oba: false
				hub_status: 'live'
				version: 2
				is_migrated: false
				settings: null
				client: {
					id: 'TvqvohYRCL'
					name: 'Rakesh Kumar Chaudhary'
					contact_info: {
						email: 'rakesh.chaudhary+360test1@edgecx.com'
					}
					partner_payload: '64e73b0cde7e2bbb69649d3e'
				}
				waba_account: {
					id: 'OJhN7lWA'
					on_behalf_of_business_info: null
					fb_account_status: 'not_verified'
					namespace: 'fcdb139c_86f1_4d36_84bf_fb85dc4ecd4b'
					external_id: '105024229369767'
					fb_business_id: '996231914910425'
				}
				integration: {
					enabled: true
					state: 'created'
					app_id: '124286'
					hosting_platform_type: 'meta_cloud_api'
				}
			}
		}
		channel_created?: {
			id: 'wFp6532f4pBrTwDM'
			event: 'channel_created'
			data: {
				id: 'MTBICFCH'
				setup_info: {
					phone_number: '919472045918'
					phone_name: 'Zsites Enterprises'
				}
				status: 'ready'
				account_mode: 'live'
				created_at: '2023-08-24T11:31:13Z'
				billing_started_at: null
				cancelled_at: null
				terminated_at: null
				client_id: 'TvqvohYRCL'
				current_quality_rating: null
				current_limit: null
				has_inbox: false
				is_oba: false
				hub_status: 'live'
				version: 2
				is_migrated: false
				settings: null
				client: {
					id: 'TvqvohYRCL'
					name: 'Rakesh Kumar Chaudhary'
					contact_info: {
						email: 'rakesh.chaudhary+360test1@edgecx.com'
					}
					partner_payload: '64e73b0cde7e2bbb69649d3e'
				}
				waba_account: {
					id: 'OJhN7lWA'
					on_behalf_of_business_info: null
					fb_account_status: 'not_verified'
					namespace: 'fcdb139c_86f1_4d36_84bf_fb85dc4ecd4b'
					external_id: '105024229369767'
					fb_business_id: '996231914910425'
				}
				integration: {
					enabled: true
					state: 'created'
					app_id: '124286'
					hosting_platform_type: 'meta_cloud_api'
				}
			}
		}
		channel_live?: {
			id: 'cNexZf4MVHHya3DM'
			event: 'channel_live'
			data: {
				id: 'MTBICFCH'
				setup_info: {
					phone_number: '919472045918'
					phone_name: 'Zsites Enterprises'
				}
				status: 'ready'
				account_mode: 'live'
				created_at: '2023-08-24T11:31:13Z'
				billing_started_at: null
				cancelled_at: null
				terminated_at: null
				client_id: 'TvqvohYRCL'
				current_quality_rating: null
				current_limit: null
				has_inbox: false
				is_oba: false
				hub_status: 'live'
				version: 2
				is_migrated: false
				settings: null
				client: {
					id: 'TvqvohYRCL'
					name: 'Rakesh Kumar Chaudhary'
					contact_info: {
						email: 'rakesh.chaudhary+360test1@edgecx.com'
					}
					partner_payload: '64e73b0cde7e2bbb69649d3e'
				}
				waba_account: {
					id: 'OJhN7lWA'
					on_behalf_of_business_info: null
					fb_account_status: 'not_verified'
					namespace: 'fcdb139c_86f1_4d36_84bf_fb85dc4ecd4b'
					external_id: '105024229369767'
					fb_business_id: '996231914910425'
				}
				integration: {
					enabled: true
					state: 'created'
					app_id: '124286'
					hosting_platform_type: 'meta_cloud_api'
				}
			}
		}
		client_created?: {
			id: 'WE5zmjCrNgmlybDM'
			event: 'client_created'
			data: {
				client: {
					id: 'alFBPyzKCL'
					name: 'Rakesh Kumar Chaudhary'
					contact_info: {
						email: 'rakesh.chaudhary+360test@edgecx.com'
					}
					partner_payload: '64e73b0cde7e2bbb69649d3e'
				}
			}
		}
	}

	pinnacleApiKey?: string
}

/**
 * Type for Single Client Dashboard using the WhatCX Application
 */

export interface SingleClientProfileDashboard {
	adminCount: number
	teamCount: number
}

/**
 * Type of Metadata in Client Wallet Transaction
 */

// export interface WalletMeta {
// 	amount: number
// 	amount_due: number
// 	amount_paid: number
// 	attempts: number
// 	createdBy: string
// 	created_at: string | number
// 	currency: Currency
// 	entity: string
// 	id: string | number
// 	notes: []
// 	offer_id: null | string
// 	receipt: string
// 	status: string
// }

/**
 * Type Definition for Client Wallet Transaction
 */
export interface ClientWalletTransaction {
	_id: string
	visible: boolean
	walletRef: string
	meta: {
		[key: string]: any
	}
	remarks: string
	type: TransactionType
	amount: number
	createdAt: string
	updatedAt: string
	__v: number
	broadcast: string | null
	contact: string | null
	conversation: string | null
	conversation_track: string | null
	module: Modules
	txn_currency: CurrencyType
	cost?: {
		total: number
		amount: number
		mediaCharge: number
		mediaSize: number
		chargeType: 'UIC' | 'BIC'
		currency: CurrencyType
		taxAmount: number
		taxation: {
			key: string
			percentage: string
			value: number
		}[]
	}
	// TODO: Ask what this is
	transactionType: string
}

/**
 * Type Definition for Client Wallet Transaction main data
 */

export interface clientWalletTransactionData {
	totalCount: number
	records: ClientWalletTransaction[] | []
	totalPages: number
	currentPage: number
}

export interface LocationMessage {
	latitude: number
	longitude: number
}

/**
 * Types of template statuses that can exist
 */

export type TemplateStatus =
	| 'DRAFT'
	| 'CREATE_REQUEST'
	| 'PENDING'
	| 'APPROVED'
	| 'REJECTED'
	| 'REQUEST_TO_APPROVE'
	| 'DELETE_REQUEST'
	| 'DELETED'
	| 'PAUSED'
	| 'DISABLED'
	| 'FLAGGED'
	| 'IN_APPEAL'
	| 'REINSTATED'

/**
 * Types of Template
 */

export type TemplateType = 'TEXT' | 'IMAGE' | 'DOCUMENT' | 'VIDEO'

/**
 * Type of Template category
 */

export type TemplateCategory =
	| 'ACCOUNT_UPDATE'
	| 'PAYMENT_UPDATE'
	| 'PERSONAL_FINANCE_UPDATE'
	| 'SHIPPING_UPDATE'
	| 'RESERVATION_UPDATE'
	| 'ISSUE_RESOLUTION'
	| 'APPOINTMENT_UPDATE'
	| 'TRANSPORTATION_UPDATE'
	| 'TICKET_UPDATE'
	| 'ALERT_UPDATE'
	| 'AUTO_REPLY'

/**
 * Template Action Types
 */

export type TemplateActionTypes = 'CALL_TO_ACTION' | 'QUICK_REPLY'

/**
 * Template Call To Action Single Option
 */
export interface TemplateCallAction {
	type: 'PHONE_NUMBER' | 'URL'
	button_title: string
	url_type: 'NA' | 'STATIC' | 'DYNAMIC'
	button_value: string
}

/**
 *  Type Definition for Single Template Action
 */

export interface TemplateAction {
	type: TemplateActionTypes
	items: TemplateCallAction[] | string[]
}

/**
 * Type Definition for Attachment Type For Templates
 */

export interface AttachmentType {
	link: string
	fileName?: string
}

/**
 * Type Definition for Single Template Object
 */

export interface TemplateParameterSelection {
	selection: 'fixed_value' | 'unique_value' | string | null
	fallback: string
}

export interface Template {
	document: any
	_id: string
	action: TemplateAction | null
	key: string
	isDeleted: boolean
	status: TemplateStatus
	isAction: boolean
	createdBy: string
	meta: {
		[key: string]: any
	}
	vendorTemplateId: string
	vendor: string
	// TODO: Add Proper Type for History
	history: any[]
	remarks: null | string
	parameterSelection?: TemplateParameterSelection[]
	client: {
		email: string
		organizationName: string
		_id: string
	}
	location: LocationMessage | null
	video: AttachmentType | null
	image: AttachmentType | null
	sampleMessage: string
	formattedTemplate: string
	elementName: string
	language: {
		label: string
		value: string
	}
	header: null | {
		type: 'TEXT' | string
		text: string
		params: Array<string>
	}
	is_dynamic_btn_url: null | boolean
	type: TemplateType
	category: TemplateCategory
	label: string
	webhooks: []
	headerSelection?: TemplateParameterSelection[]
	urlParameterSelection?: TemplateParameterSelection[]
	createdAt: string
	updatedAt: string
	__v: number
}

/** Type Definition for Single Invoice Object */
export interface Invoice {
	_id: string
	createdByMeta: {
		name: string
		_id: string
		email: string
	}
	selectedPlan: Plan
	number: string
	invoiceHistory: any[]
	meta: {
		[key: string]: any
	}
	shouldDeactivateAccount?: boolean
	isDeleted: boolean
	payUrl: string
	url: string
	isPaid: boolean
	paidAmount: number
	createdBy: string
	dueDate: string | null
	endDate: string | null
	startDate: string | null
	client: ClientProfile
	amount: number
	currency?: Currency
	isSystemGeneratedInvoice?: boolean
	createdAt: string
	updatedAt: string
	__v: number
	tdsEnabled?: boolean
	tdsAmount?: number
}

/** Type Definition of Single Payment */

export interface Payment {
	acceptedBy: string | null
	acceptedByMeta: {
		email: string
		name: string
		_id: string
	} | null
	amount: number
	client: ClientProfile
	createdAt: string
	invoice: string | Invoice
	meta: { [key: string]: any } | null
	paymentId: string
	remarks: string | null
	txnChannel: string
	txnMode: string
	txnType: string
	updatedAt: string
	url: string | null
	wallet: string | null
	currency?: Currency
	__v: number
	_id: string
}

/** Type Definition of week day */

export type WeekDay =
	| 'SUNDAY'
	| 'MONDAY'
	| 'TUESDAY'
	| 'WEDNESDAY'
	| 'THURSDAY'
	| 'FRIDAY'
	| 'SATURDAY'
	| string

/** Type Definition of Single Working Hour Object */

export interface WorkingHour {
	day: WeekDay
	start: string
	end: string
}

/** Type Definition of Role */

export interface ClientRole {
	__v: 0
	_id: string
	client: string
	createdAt: string
	key: string
	permission: null
	role: string
	roleSlug: string
	roleType: string
	status: boolean
	updatedAt: string
	visible: boolean
}

/** Type Definition of Single Client User */

export interface ClientUser {
	_id: string
	website: string | null
	organizationName: string | null
	avatarBackgroundColor: string
	teamBelongsTo: string[]
	defaultContactView: string
	profileImage: string
	active: boolean
	belongsTo: string
	twoFactorAuth: boolean
	role: null | string
	lastLogin: string
	lastName: string
	firstName: string
	mobileNumber: MobileNumber
	email: string
	workingHours: WorkingHour
	createdAt: string
	updatedAt: string
	__v: 0
}

/** Type Definition for Single Client WhatsApp Details */

export interface ClientWhatsAppDetails {
	verified_name: string
	code_verification_status: string
	display_phone_number: string
	quality_rating: string
	address: string
	description: string
	email: string
	profile_picture_url: string | null
	websites: string[]
	vertical: string | null
}

/**
 * Single Message
 */

/**
 * Types of messages that can be sent
 */

export type MessageType =
	| 'TEXT'
	| 'MEDIA_IMAGE'
	| 'MEDIA_VIDEO'
	| 'MEDIA_AUDIO'
	| 'MEDIA_DOCUMENT'
	| 'LOCATION'
	| 'CONTACT'

export interface DocumentMessage {
	url: string
	fileName: string
	caption?: string
}

export interface ImageMessage {
	link: string
	caption: string
}

export interface VideoMessage {
	link: string
	caption: string
}

export interface AudioMessage {
	link: string
}

export interface ContactAddress {
	country_code: string
	street: string
	type: 'Home' | 'Work' | 'Other'
}

export interface ContactEmail {
	email: string
}

export interface ContactPhone {
	phone: string
	type: string
	wa_id: string
}

export interface ContactOrganization {
	company: string
	title?: string
}

export interface ContactUrl {
	url: string
	type: string
}

export interface ContactMessage {
	addresses?: ContactAddress[]
	emails?: ContactEmail[]
	ims?: null
	name?: {
		first_name: string
		last_name: string
		formatted_name: string
		middle_name?: string
		suffix?: string
	}
	phones?: ContactPhone[]
	urls?: ContactUrl[]
	org?: ContactOrganization
}

export interface MessageDecoration {
	type: string
	text: string
}

// Same props for QuickReplyButton
export interface QuickReplyButton {
	type: 'text' // TODO: Add support for other types
	title: string
}

export interface MessageCallToActions {
	type: 'URL' | 'PHONE_NUMBER'
	button_title: string
	url_type?: 'STATIC' | 'DYNAMIC' | 'NA'
	button_value?: string
	url_parameter?: string
}

/**
 * List Type Message
 */

export interface ListTypeSectionRow {
	id: string
	title: string
	description?: string
	product_items?: Array<any>
}

export interface ListTypeSection {
	title?: string
	rows?: Array<ListTypeSectionRow>
}

export interface ListTypeOptions {
	button: string
	sections: Array<ListTypeSection>
}

export interface Message {
	type: MessageType
	document: null | DocumentMessage
	location: null | LocationMessage
	video: null | VideoMessage
	audio: null | AudioMessage
	message: string | null
	image: null | ImageMessage
	contacts: null | ContactMessage[]
	footer?: MessageDecoration
	header?: MessageDecoration
	options?: QuickReplyButton[]
	callToActions?: MessageCallToActions[]
	listOptions?: ListTypeOptions
}

/**
 * Broadcast Statuses
 */

export type BroadcastStatus =
	| 'DRAFT'
	| 'SCHEDULED'
	| 'COMPLETED'
	| 'CANCELED'
	| 'IN_PROCESS'

/**
 * Broadcast Audience Type
 */

export type BroadcastAudienceType =
	| 'ALL_OPTED_IN'
	| 'VIEW_SELECTED'
	| 'TAG_SELECTED'
	| 'IMPORTED_FILE'
	| 'INTELLIGENT_AUDIENCE'
	| 'UPLOAD'

/**
 * Broadcast Contact Attachment
 */

export interface BroadcastContactAttachment {
	url: string
	fileName?: string
}

/**
 * Broadcast Contact status
 */

export type BroadcastContactsStatus =
	| 'ENQUEUED'
	| 'SENT'
	| 'DELIVERED'
	| 'READ'
	| 'RECEIVED'
	| 'FAILED'
	| 'DELETED'

/**
 * BroadcastContact Conversation
 */

export interface BroadcastContactConversation {
	_id: string
	statusHistory: string[]
	message: {
		type: 'TEXT' | 'MEDIA'
		message: string
	}
	status: BroadcastContactsStatus
	source: string
	remarks: string | null
}

/**
 * Broadcast Contact Link
 */
export interface BroadcastContactLink {
	_id: string
	client: string
	admin: null | string
	broadcast: null | string
	broadcast_contact: null | string
	contact: null | string
	conversation: null | string
	module: string
	longUrl: string
	shortUrl: string
	timeStamp: number
	qrcode: null | string
	host: string
	url: string
	insight: {
		total: number
		mobile: number
		qr: number
		source: [
			{
				source: string
				count: number
			}
		]
		browser: [
			{
				browser: string
				count: number
			}
		]
		os: [
			{
				os: string
				count: number
			}
		]
		country: [
			{
				country: string
				count: number
			}
		]
		device:
			| []
			| [
					{
						device: string
						count: number
					}
			  ]
	}
	createdAt: string
	updatedAt: string
	__v: number
}

/**
 * BroadcastContacts Message Cost
 */

// export interface BroadcastContactMessageCost {
// 	total: number
// 	mediaCharge: number
// 	mediaSize: number
// 	chargeType: 'UIC' | 'BIC'
// 	currency: CurrencyType
// }

export interface Permission {
	conversationPermission: {
		reassign_conversation: {
			to_assign_self: boolean
			to_assign_others: boolean
		}
		access_conversation: {
			access_active_conversation: boolean
			access_whatsy_conversation: boolean
			access_team_conversation: boolean
			access_inactive_conversation: boolean
			access_assigned_conversation: boolean
			access_feedback_conversation: boolean
			access_enquiry_conversation: boolean
			access_template_conversation: boolean
			access_session_conversation: boolean
			access_integration_conversation: boolean
			access_broadcast_conversation: boolean
			access_form_conversation: boolean
		}
		can_access_module: boolean
		can_access_all_conversation: boolean
		can_change_team: boolean
		can_access_bot: boolean
		can_trigger_form: boolean
	}
	contactPermission: {
		single_contact: {
			can_edit: boolean
			can_edit_email: boolean
			can_edit_mobile: boolean
			can_block: boolean
			can_read_email: boolean
			can_read_mobile: boolean
			can_read: boolean
			can_delete: boolean
		}
		contact_dashboard: {
			can_manage_contact_views: boolean
			can_export: boolean
			can_import: boolean
			can_add: boolean
			can_read: boolean
			can_access_assigned_contacts?: boolean
			can_access_others_contact?: boolean
			can_access_team_contacts?: boolean
			can_delete?: boolean
		}
	}
	enquiriesPermission: {
		can_update: boolean
		can_read: boolean
		can_export: boolean
		can_configure_module: boolean
		can_access_module: boolean
	}
	feedbackPermission: {
		can_export: boolean
		can_configure_module: boolean
		can_access_module: boolean
	}
	whatsyPermission: {
		can_update: boolean
		can_delete: boolean
		can_create: boolean
		can_read: boolean
	}
	templatePermission: {
		can_update: boolean
		can_delete: boolean
		can_create: boolean
		can_read: boolean
	}
	broadcastPermission: {
		can_reschedule: boolean
		can_schedule: boolean
		can_access_module: boolean
		can_edit: boolean
		can_cancel: boolean
		can_add: boolean
		can_view: boolean
	}
	notifierPermission?: {
		can_create?: boolean
		can_read?: boolean
		can_update?: boolean
		can_delete?: boolean
		can_retry?: boolean
	}
	dripPermission?: {
		can_create?: boolean
		can_read?: boolean
		can_update?: boolean
		can_delete?: boolean
		can_retry?: boolean
	}
	adminAccessPermission: {
		global_setting: {
			office_hour_setting: boolean
			assignment_setting: boolean
			account_setting: boolean
			current_plan_setting: boolean
		}
		wallet_access: {
			balance: boolean
			transaction: boolean
			add_to_wallet: boolean
		}
		billing_access: {
			invoices: boolean
			payments: boolean
		}
		tags_access: {
			can_create: boolean
			can_read: boolean
			can_update: boolean
			can_delete: boolean
		}
		conversationLabelPermission: {
			can_create: boolean
			can_read: boolean
			can_update: boolean
			can_delete: boolean
		}
		custom_field_access: {
			can_create: boolean
			can_read: boolean
			can_update: boolean
			can_delete: boolean
		}
		team_access: {
			can_create: boolean
			can_read: boolean
			can_update: boolean
			can_delete: boolean
		}
		role_access: {
			can_create: boolean
			can_read: boolean
			can_update: boolean
			can_delete: boolean
		}
		user_access: {
			can_create: boolean
			can_read: boolean
			can_update: boolean
			can_delete: boolean
		}
	}
	qrWidgetPermission: {
		can_create: boolean
		can_delete: boolean
		can_read: boolean
		can_update: boolean
	}
	manageAppIntegration?: {
		can_access: boolean
	}
	formPermission: {
		can_read: boolean
		can_create: boolean
		can_update: boolean
		can_access_form_entries: boolean
		can_exports_form_entries: boolean
	}
	_id: string
	createdAt: string
	updatedAt: string
}

export interface Role {
	visible: boolean
	client: string | null
	roleType: 'SYSTEM' | 'CLIENT'
	status: boolean
	roleSlug: string
	key: string
	role: string
	usedCount: number
	permission: Permission
	_id: string
	createdAt: string
	updatedAt: string
	__v: number
}

/**
 * Reply Intents
 */

export type ReplyIntent =
	// ! TRIGGER_WHATSY has been deprecated. Kindly use TRIGGER_BOT Instead
	| 'TRIGGER_WHATSY'
	| 'TRIGGER_ENQUIRY'
	| 'ASSIGN_TO_TEAM'
	| 'ASSIGN_TO_AGENT'
	| 'TRIGGER_FORM'
	| 'TRIGGER_BOT'
	| 'TRIGGER_CHATBLOCK'
	| 'TRIGGER_SAME_BOT_OPTION'
	| 'TRIGGER_PARENT_BOT'
	| 'NO_ACTION'

export interface QuickReplyAction {
	intent: ReplyIntent | null
	teamId?: string | null
	whatsyId?: string | null
	agents?: string[]
	enquiryId?: null | string
	responseMessage?: Message[]
	tags: string[]
	chatLabels: string[]
	formId?: string | null
	botGroupId?: string | null
	skipBotWelcomeMessage?: boolean
}

export interface QuickReplyIntent {
	index: number
	action: QuickReplyAction
	key?: string
}

/**
 * Single Broadcast
 */

export interface Broadcast {
	_id: string
	deleted: null | boolean
	defaultMessage: null | Message
	time: null | string
	contactSelected: number
	cost: number
	template: null | string | Template
	createdBy: string
	client: string
	key: string
	status: BroadcastStatus
	name: string
	tags: string[]
	enquiry: boolean
	isTimeSensitive: boolean
	autoAssignAgents: string[]
	autoAssignTeam: string | null
	audienceType: null | BroadcastAudienceType
	query_payload: null | {
		queryType: BroadcastAudienceType
		tag: string | null
		view: string | null
		file: string | null
		selection: string | null
		broadcast: string
		dry_run: boolean
	}
	// contactFilter: {
	//   orFilter: FilterOption[];
	//   andFilter: FilterOption[];
	// } | null;
	contactFilter: null | {
		client?: string
		source?: string
		sourceId?: {
			$in: string[]
		}
		csv_file?: {
			key: string
			name: string
		}
	}
	mediaSelectionType: 'STATIC' | 'DYNAMIC'
	currentStep: number
	totalFailed: number
	totalSent: number
	totalProcessed: number
	createdAt: string
	updatedAt: string
	processCompletedAt: null | string
	processStartedAt: null | string
	completionTime: number
	chunkProcessed: number
	templateParameter: null
	broadcast_uid: string
	cost_consumed: number
	creation_source: 'SMART_BROADCAST' | 'BASIC_BROADCAST'
	currency: null | CurrencyType
	__v: number
	// Keys added with Broadcast Reply Intent
	// TODO Figure out what some of them do.
	headerParameter?: []
	urlParameter?: []
	replySetting?: 'ACTION' | 'NON_ACTION'
	agentsRemoved?: []
	replyActionIntentSetting?: QuickReplyIntent[]
}

export interface BroadcastWithClientData extends Omit<Broadcast, 'client'> {
	client: Partial<ClientProfile>
}

export interface BroadcastInsights {
	deliveredCount: number
	enquiry: number
	failedCount: number
	readCount: number
	replies: number
	sendCount: number
}

export type BroadcastViews = Record<BroadcastStatus, number>

export interface BroadcastContactMessageCost {
	total: number
	mediaCharge: number
	mediaSize: number
	chargeType: 'UIC' | 'BIC'
	currency: CurrencyType
}

export interface BroadcastContact {
	_id: string
	params: string[]
	broadcast: string
	contact: {
		_id: string
		email: string | null
		firstName: string
		is_whatsapp_contact: boolean
		lastName?: string
		middleName?: string
		mobile: MobileNumber
		sessionExpiredAt: string
	}
	source: null | string
	media_url: string | null
	fileName: string | null
	meta: null | object
	conversation: BroadcastContactConversation | null
	chunk: null | number
	payload: null | object
	is_deleted: null | boolean
	message_cost: null | BroadcastContactMessageCost
	broadcast_status: null | 'failed' | 'sent'
	createdAt: string
	updatedAt: string
	links: BroadcastContactLink[] | []
	dynamic_url_position: number
	header_params: string[]
	is_billed: boolean
	reason: null | string
	url_params: string[]
	__v: number
}

/**
 * Possible Enquiry Status
 */
export type EnquiryStatus = 'NEW' | 'PROCESSING' | 'ESCALATED' | 'CLOSED'

export type EnquiryModule = 'BROADCAST' | 'FEEDBACK' | 'CONVERSATION'

export interface EnquiryContact {
	_id: string
	lastName: null | string
	firstName: string
	mobile: MobileNumber
}

/**
 * Type Definition for Enquiries
 */

export interface Enquiry {
	_id: string
	broadcast?: string
	last_sla_ts?: number
	// TODO: Update this to be a proper type
	team?: null
	lastSla: null | string
	module: EnquiryModule
	client: string
	meta: {
		[key: string]: any
	}
	status: EnquiryStatus
	message: string
	contact: EnquiryContact
	enquiryId: string
	processed: boolean
	createdAt: string
	updatedAt: string
	__v: number
}

export interface BroadcastReplyMessage {
	status: string
	senderSource: string
	message: {
		type: 'TEXT' | 'MEDIA'
		message: string
	}
	source: string
	contact: string
	client: string
	payload: { [key: string]: any } | null
	statusHistory: string[]
	eventHistory: { [key: string]: any } | null
	externalMessageId: string
	session: string
}

interface Parameter extends TemplateParameterSelection {}

export interface BroadcastReplyUser {
	firstName: string
	lastName: string
	middleName: string
	email: string
	mobile: MobileNumber
	_id: string
}

export interface BroadcastReplyType {
	user: BroadcastReplyUser
	message: BroadcastReplyMessage
	broadcast: string
	isButtonReply: boolean
	createdAt: string
	updatedAt: string
	__v: number
	_id: string
}

export interface UpdateBroadcastBody {
	templateParameter: Parameter[]
	headerParameter: Parameter[]
	urlParameter: Parameter[]
	audienceType: BroadcastAudienceType | null
	isTimeSensitive: boolean
	time: string | null
	template: string | null
	currentStep: number
	mediaSelectionType: 'STATIC' | 'DYNAMIC'
	media_url: null | string
	fileName: null | string
	shouldSchedule: boolean
	autoAssignTeam: null | string
	autoAssignAgents: string[]
	enquiry: boolean
	tags: string[]
	defaultMessage: null | Message
	qrBtnActionSetting: QuickReplyIntent[]
}
export interface PlanDetails {
	id: string
	title: string
	userAllowed: number
	teamAllowed: number
	templateAllowed: number
	storageAllowed: number
	billingCycle: 'MONTHLY' | 'YEARLY'
	perMonth: number
	perYear: number
	allowedModules: AllowedModule[]
}
export interface WebhookJsonDetailsI {
	whatsapp_business_api_data: {
		link: string
		name: string
		id: string
		category?: string
	}
	override_callback_uri?: string
}

export interface PaginationState {
	currentPage: number
	totalCount: number
	pageSize: number
}

// Waba management api

export interface WebhookConfiguration {
	whatsapp_business_account: string
	application: string
}
export interface Throughput {
	level: string
}

export interface WabaManagementApiResponse {
	webhook_configuration: WebhookConfiguration
	name_status: string
	status: string
	display_phone_number: string
	quality_rating: string
	verified_name: string
	code_verification_status: string
	throughput: Throughput
	id: string
}

export interface CreateSubscriptionBody {
	title: string
	billingCycle: BillingCycle
	amount: number
	currency: Currency
	description: string
	plan: {
		title: string
		storageAllowed: number
		billingCycle: BillingCycle
		perMonth: number
		perYear: number
		modules: AllowedModule[]
	}
}

export interface SubscriptionResponse {
	vendor: string
	billingCycle: BillingCycle
	amount: number
	currency: string
	status: string
	metaPayload: {
		razorpay: {
			id: string
			entity: string
			interval: number
			period: string
			item: {
				id: string
				active: boolean
				name: string
				description: string
				amount: number
				unit_amount: number
				currency: string
				type: string
				unit?: number | null
				tax_inclusive: boolean
				hsn_code?: any
				sac_code?: any
				tax_rate?: any
				tax_id?: any
				tax_group_id?: any
				created_at: number
				updated_at: number
			}
			notes: {
				ref: string
				plan: string
			}
			created_at: number
		}
		body: {
			title: string
			billingCycle: BillingCycle
			amount: number
			currency: string
			description: string
			plan: {
				title: string
				storageAllowed: number
				billingCycle: BillingCycle
				perMonth: number
				perYear: number
				modules: AllowedModule[]
			}
		}
		plan: {
			title: string
			storageAllowed: number
			billingCycle: BillingCycle
			perMonth: number
			perYear: number
			modules: null
			allowedModules: AllowedModule[]
			planName: string
			templateAllowed: number
		}
	}
	planTitle: string
	vendorPlanId: string
	planUid: string
	plan: {
		title: string
		storageAllowed: number
		billingCycle: BillingCycle
		perMonth: number
		perYear: number
		modules: AllowedModule[]
	}
	description: string
	_id: string
	createdAt: string
	updatedAt: string
	__v: number
}

export interface SubscriptionData {
	_id: string
	vendor: string
	billingCycle: BillingCycle
	amount: number
	currency: string
	status: string
	planTitle: string
	vendorPlanId: string
	planUid: string
	description: string
	createdAt: string
	updatedAt: string
	__v: number
}
